/**
 * Operator-attributed conversion facts for a landing page.
 *
 * ⚠ THIS IS NOT THE TWEAKS PANEL. Rating, review count and guarantee text
 * license claims on a live client's paid page. BuilderTweaks is written
 * never to send those keys — a tick box labelled "tweaks" that lets someone
 * type a star rating nobody earned is how an unverified claim looks like a
 * formatting choice. The question six weeks later is who said it, which is
 * why confirmed_by is required and an empty name refuses the write.
 *
 * Named prices and offers render when attributed. The copy engines still
 * refuse to invent them. "Best price" with no record stays off the page.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { landingPagesService } from '@/services/landingPagesService';
import type { LandingArtFactsPayload } from '@/services/landingPagesService';

import { ART_PLATFORMS, buildArtFactsPayload } from './artFactsPayload';

interface Props {
  tenantId: string;
}

const inputClass = 'w-full rounded-md border border-border px-2 py-1.5 text-sm';

export default function ArtIntake({ tenantId }: Props) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['landing-art-facts', tenantId],
    queryFn: () => landingPagesService.artFacts(tenantId),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  const [who, setWho] = useState('');
  const [rating, setRating] = useState('');
  const [count, setCount] = useState('');
  const [platform, setPlatform] = useState('Google');
  const [captured, setCaptured] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [guarantee, setGuarantee] = useState('');
  const [hours, setHours] = useState('');
  const [is247, setIs247] = useState(false);
  const [offer, setOffer] = useState('');
  const [offerExpires, setOfferExpires] = useState('');
  const [price, setPrice] = useState('');

  useEffect(() => {
    if (!data) return;
    const f = data.facts || {};
    setRating(f.proof?.rating || '');
    setCount(f.proof?.reviews_count || '');
    setPlatform(f.proof?.platform || 'Google');
    setCaptured(f.proof?.captured_at || '');
    setSourceUrl(f.proof?.source_url || '');
    setGuarantee(f.guarantee?.text || '');
    setHours(f.hours?.text || '');
    setIs247(Boolean(f.hours?.is_24_7));
    setOffer(f.offer?.text || '');
    setOfferExpires(f.offer?.expires || '');
    setPrice(f.entry_price?.text || f.entry_price?.amount || '');
    // Do not prefill the previous operator's name. Attribution is this save,
    // and the authenticated actor is recorded separately on the server.
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const facts: LandingArtFactsPayload = buildArtFactsPayload({
        rating,
        count,
        platform,
        sourceUrl,
        captured,
        guarantee,
        hours,
        is247,
        offer,
        offerExpires,
        price,
      });
      return landingPagesService.saveArtFacts(tenantId, facts, who.trim());
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['landing-art-facts', tenantId] }),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading sourced facts…</p>;
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load sourced facts: {String((error as Error)?.message)}
      </p>
    );
  }

  return (
    <div className="max-w-xl space-y-6">
      <p className="text-sm text-muted-foreground">
        Facts that make a page look credible above the fold — Google stars,
        a real guarantee, hours — only when a named person records them.
        Empty stays empty. The Tweaks panel still cannot license these.
      </p>

      <fieldset className="space-y-3 rounded-md border border-border p-4">
        <legend className="px-1 text-sm font-medium">Google reviews</legend>
        <p className="text-sm text-muted-foreground">
          Rating, count, platform, the listing URL you copied, and the date
          you saw it. A typed number with no URL is not a fact — the write
          is refused, not stored.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            Rating
            <input
              value={rating}
              onChange={(e) => setRating(e.target.value)}
              placeholder="4.9"
              inputMode="decimal"
              className={`${inputClass} mt-1`}
            />
          </label>
          <label className="text-sm">
            Review count
            <input
              value={count}
              onChange={(e) => setCount(e.target.value)}
              placeholder="212"
              inputMode="numeric"
              className={`${inputClass} mt-1`}
            />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            Platform
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className={`${inputClass} mt-1`}
            >
              {ART_PLATFORMS.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            Seen on
            <input
              value={captured}
              onChange={(e) => setCaptured(e.target.value)}
              placeholder="2026-08-28"
              className={`${inputClass} mt-1`}
            />
          </label>
        </div>
        <label className="block text-sm">
          Listing URL
          <input
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="https://maps.google.com/?cid=…"
            inputMode="url"
            className={`${inputClass} mt-1`}
          />
        </label>
      </fieldset>

      <label className="block text-sm font-medium">
        Guarantee (verbatim)
        <textarea
          value={guarantee}
          onChange={(e) => setGuarantee(e.target.value)}
          rows={2}
          placeholder="Lifetime workmanship on labour"
          className={`${inputClass} mt-1`}
        />
      </label>

      <label className="block text-sm font-medium">
        Hours
        <input
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          placeholder="Mon–Sat 7am–7pm"
          className={`${inputClass} mt-1`}
        />
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={is247}
          onChange={(e) => setIs247(e.target.checked)}
        />
        24/7 — they actually answer the phone overnight
      </label>

      <fieldset className="space-y-3 rounded-md border border-border p-4">
        <legend className="px-1 text-sm font-medium">Offer and price — shown when sourced</legend>
        <p className="text-sm text-muted-foreground">
          These appear on the page only after you save them here. The
          generator will not invent “best price” or a dollar figure.
          An offer needs a future expiry date (YYYY-MM-DD) — the box is
          time-bound by definition, including a named credit. A lapsed
          date is refused, not stored.
        </p>
        <label className="block text-sm">
          Named offer
          <input
            value={offer}
            onChange={(e) => setOffer(e.target.value)}
            placeholder="$89 service call credited to the job"
            className={`${inputClass} mt-1`}
          />
        </label>
        <label className="block text-sm">
          Offer expires
          <input
            value={offerExpires}
            onChange={(e) => setOfferExpires(e.target.value)}
            placeholder="2026-09-30"
            className={`${inputClass} mt-1`}
          />
        </label>
        <label className="block text-sm">
          Entry price
          <input
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="from $89"
            className={`${inputClass} mt-1`}
          />
        </label>
      </fieldset>

      <label className="block text-sm font-medium">
        Your name
        <input
          value={who}
          onChange={(e) => setWho(e.target.value)}
          placeholder="Who is recording this"
          className={`${inputClass} mt-1`}
          required
        />
      </label>

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!who.trim() || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-md bg-coral px-4 py-2 text-sm font-medium text-white hover:bg-coral-hover disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save sourced facts'}
        </button>
        {save.isSuccess && (
          <span className="text-sm text-teal-dark">
            Saved{save.data?.proof_ready ? ' — stars can render' : ' — stars still hidden'}
            {save.data?.guarantee_ready ? '; guarantee licensed' : ''}
            {save.data?.available_now_ready ? '; available now licensed' : ''}
            {save.data?.price_ready ? '; price can render' : ''}
            {save.data?.offer_ready ? '; offer can render' : ''}.
          </span>
        )}
        {save.isError && (
          <span className="text-sm text-destructive">
            {String((save.error as Error)?.message)}
          </span>
        )}
      </div>
    </div>
  );
}
